import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import ProgressionSection from './fitness/ProgressionSection';
import { getStoredWorkoutUserId, listActiveWorkoutDrafts } from '../utils/activeWorkoutDrafts';
import { getMyCoaches, unsubscribeFromCoach } from '../services/nutritionService';
import { getUnreadCounts } from '../services/messageService';
import ConfirmModal from './ConfirmModal';
import { useWeightUnit } from '../hooks/useWeightUnit';
import { gymKgToDisplay } from '../utils/weightUnits';
import ChatPanel from './ChatPanel';
import './MyWorkoutsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/** Coach assignments may populate refs as objects or leave Mongo ids as strings */
const resolveWorkoutRouteId = (ref) => {
  if (ref == null) return null;
  if (typeof ref === 'string') return ref;
  return ref.workoutId || (ref._id != null ? String(ref._id) : null);
};

const resolveProgramRouteId = (ref) => {
  if (ref == null) return null;
  if (typeof ref === 'string') return ref;
  return ref.programId || (ref._id != null ? String(ref._id) : null);
};

const normalizeAssignmentType = (t) => (t && String(t).toLowerCase()) || '';

const resolveExerciseRouteId = (ref) => {
  if (ref == null) return null;
  if (typeof ref === 'string') return ref;
  return ref.exerciseId || (ref._id != null ? String(ref._id) : null);
};

/** Schedule row stores sessionId ref as Mongo _id; API accepts that or the string sessionId field */
const resolveSessionRouteParam = (ref) => {
  if (ref == null) return null;
  if (typeof ref === 'string') return ref;
  if (ref.sessionId) return ref.sessionId;
  if (ref._id != null) return String(ref._id);
  return null;
};

/** Prefer actual refs over `type` (handles bad/missing type on older rows) */
const inferAssignmentContent = (item) => {
  if (item.workoutId) return 'workout';
  if (item.programId) return 'program';
  if (item.exerciseId) return 'exercise';
  return null;
};

const canEditOwnedWorkoutOnSchedule = (item, sub) => {
  if (!sub || inferAssignmentContent(item) !== 'workout') return false;
  const w = item.workoutId;
  if (!w || typeof w !== 'object' || !w._id) return false;
  return w.userId === sub;
};

const canEditOwnedProgramOnSchedule = (item, sub) => {
  if (!sub || inferAssignmentContent(item) !== 'program') return false;
  const p = item.programId;
  if (!p || typeof p !== 'object' || !p._id) return false;
  return p.userId === sub;
};

const resolveScheduledWorkoutRouteId = (item, draftByScheduleId) => {
  const fromRef = resolveWorkoutRouteId(item.workoutId);
  if (fromRef) return fromRef;
  if (item.effectiveWorkoutId) return String(item.effectiveWorkoutId);
  const draft = draftByScheduleId.get(String(item._id));
  if (draft?.workoutId) return String(draft.workoutId);
  return null;
};

// Keep selected week while navigating around the app. Hard refresh resets this.
let lastViewedWeekStartMs = null;

const MyWorkoutsPage = () => {
  const navigate = useNavigate();
  const weightUnit = useWeightUnit();
  const [weekStart, setWeekStart] = useState(() => {
    if (typeof lastViewedWeekStartMs === 'number') {
      return new Date(lastViewedWeekStartMs);
    }
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  });
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentSessions, setRecentSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [activeDrafts, setActiveDrafts] = useState([]);
  const [workoutNameByRouteId, setWorkoutNameByRouteId] = useState({});
  const [workoutDurationByRouteId, setWorkoutDurationByRouteId] = useState({});
  const [workoutMetaFetchedByRouteId, setWorkoutMetaFetchedByRouteId] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [fitnessCoachRels, setFitnessCoachRels] = useState([]);
  const [unsubscribeTarget, setUnsubscribeTarget] = useState(null);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [messagingCoachRel, setMessagingCoachRel] = useState(null);
  const [coachUnreadCounts, setCoachUnreadCounts] = useState({});
  const [usageStats, setUsageStats] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem('workouts_onboarding_dismissed') === 'true'
  );

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);

  useEffect(() => {
    fetchSchedule();
  }, [weekStart]);

  const loadFitnessCoaches = async () => {
    if (!localStorage.getItem('token')) {
      setFitnessCoachRels([]);
      return;
    }
    try {
      const all = await getMyCoaches();
      setFitnessCoachRels((all || []).filter(c => ['fitness', 'both'].includes(c.coachType)));
    } catch {
      setFitnessCoachRels([]);
    }
  };

  useEffect(() => {
    fetchRecentSessions();
    fetchAssignments();
    loadFitnessCoaches();
    fetchOnboardingData();
  }, []);

  const fetchOnboardingData = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [usageRes, subRes] = await Promise.all([
        fetch(`${API_URL}/user/usage-stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/subscription/status`, { headers: getAuthHeaders() })
      ]);
      if (usageRes.ok) {
        setUsageStats(await usageRes.json());
      }
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscriptionTier(subData.user?.tier || 'free');
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
    }
  };

  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true);
    localStorage.setItem('workouts_onboarding_dismissed', 'true');
  };

  const refreshCoachUnreadCounts = async () => {
    try {
      const counts = await getUnreadCounts();
      setCoachUnreadCounts(counts || {});
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (localStorage.getItem('token')) {
      refreshCoachUnreadCounts();
      const interval = setInterval(refreshCoachUnreadCounts, 60000);
      return () => clearInterval(interval);
    }
  }, []);

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const totalCoachUnread = Object.values(coachUnreadCounts).reduce((sum, n) => sum + n, 0);
  const draftByScheduleId = useMemo(() => {
    const map = new Map();
    for (const draft of activeDrafts) {
      if (!draft?.scheduleId) continue;
      map.set(String(draft.scheduleId), draft);
    }
    return map;
  }, [activeDrafts]);

  useEffect(() => {
    lastViewedWeekStartMs = weekStart.getTime();
  }, [weekStart]);

  useEffect(() => {
    const refreshOnFocus = () => {
      setActiveDrafts(listActiveWorkoutDrafts(getStoredWorkoutUserId()));
      fetchSchedule();
      fetchAssignments();
      fetchRecentSessions();
      loadFitnessCoaches();
      refreshCoachUnreadCounts();
    };

    const refreshDrafts = () => {
      setActiveDrafts(listActiveWorkoutDrafts(getStoredWorkoutUserId()));
    };

    refreshDrafts();
    window.addEventListener('focus', refreshOnFocus);
    window.addEventListener('storage', refreshDrafts);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      window.removeEventListener('storage', refreshDrafts);
    };
  }, []);

  useEffect(() => {
    const missingWorkoutIds = [];
    const seen = new Set();

    for (const item of schedule) {
      const kind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
      if (kind !== 'workout') continue;

      const hasName = Boolean(item.workoutId && typeof item.workoutId === 'object' && item.workoutId.name);
      const hasDuration = Boolean(
        item.workoutId &&
        typeof item.workoutId === 'object' &&
        Number.isFinite(Number(item.workoutId.estimatedDuration))
      );
      if (hasName && hasDuration) continue;

      const routeId = resolveScheduledWorkoutRouteId(item, draftByScheduleId);
      if (!routeId || seen.has(routeId)) continue;
      if (workoutMetaFetchedByRouteId[routeId]) continue;
      const cachedName = Boolean(workoutNameByRouteId[routeId]);
      const cachedDuration = Number.isFinite(Number(workoutDurationByRouteId[routeId]));
      if (cachedName && cachedDuration) continue;
      seen.add(routeId);
      missingWorkoutIds.push(routeId);
    }

    if (missingWorkoutIds.length === 0) return;

    let cancelled = false;
    (async () => {
      const nameUpdates = {};
      const durationUpdates = {};
      await Promise.all(missingWorkoutIds.map(async (routeId) => {
        try {
          const response = await fetch(`${API_URL}/fitness/workouts/${encodeURIComponent(routeId)}`, {
            headers: getAuthHeaders()
          });
          if (!response.ok) return;
          const data = await response.json();
          const name = data?.name;
          const duration = Number(data?.estimatedDuration);
          if (name) nameUpdates[routeId] = name;
          if (Number.isFinite(duration) && duration > 0) durationUpdates[routeId] = Math.round(duration);
        } catch {
          // Ignore invalid/stale refs
        }
      }));

      if (cancelled) return;
      if (Object.keys(nameUpdates).length > 0) {
        setWorkoutNameByRouteId(prev => ({ ...prev, ...nameUpdates }));
      }
      if (Object.keys(durationUpdates).length > 0) {
        setWorkoutDurationByRouteId(prev => ({ ...prev, ...durationUpdates }));
      }
      setWorkoutMetaFetchedByRouteId(prev => {
        const next = { ...prev };
        for (const routeId of missingWorkoutIds) {
          next[routeId] = true;
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [schedule, draftByScheduleId, workoutNameByRouteId, workoutDurationByRouteId, workoutMetaFetchedByRouteId]);

  const fetchRecentSessions = async () => {
    setSessionsLoading(true);
    try {
      const now = new Date();
      const response = await fetch(
        `${API_URL}/fitness/sessions?year=${now.getFullYear()}&month=${now.getMonth() + 1}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.sessions || []);
        setRecentSessions(list.slice(0, 10));
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: format(weekStart, 'yyyy-MM-dd'),
        endDate: format(weekEnd, 'yyyy-MM-dd')
      });
      const response = await fetch(`${API_URL}/fitness/my-schedule?${params}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSchedule(data);
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const response = await fetch(`${API_URL}/fitness/my-assignments`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const startAssignment = async (item) => {
    const kind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
    if (kind === 'workout') {
      const wid = resolveScheduledWorkoutRouteId(item, draftByScheduleId);
      if (!wid) {
        toast.error('Could not open this workout. It may be stale after data refresh. Remove and reschedule it.');
        return;
      }
      navigate(`/fitness/active/${wid}?scheduleId=${item._id}`);
      return;
    }
    if (kind === 'program') {
      const pid = resolveProgramRouteId(item.programId);
      if (!pid) {
        toast.error('Could not open this program. Try again or contact your coach.');
        return;
      }
      navigate(`/fitness/program/${pid}`);
      return;
    }
    if (kind === 'exercise') {
      const eid = resolveExerciseRouteId(item.exerciseId);
      if (!eid) {
        toast.error('Could not open this exercise. Try again or contact your coach.');
        return;
      }
      navigate(`/fitness/exercise/${eid}`);
      return;
    }
    toast.error('This assignment cannot be opened.');
  };

  const openCoachAssignmentCard = (item) => {
    const kind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
    if (kind === 'workout') {
      const wid = resolveScheduledWorkoutRouteId(item, draftByScheduleId);
      if (!wid) {
        toast.error('Could not open this workout. It may be stale after data refresh. Remove and reschedule it.');
        return;
      }
      navigate(`/fitness/workout/${wid}?scheduleId=${encodeURIComponent(item._id)}`);
      return;
    }
    if (kind === 'program') {
      const pid = resolveProgramRouteId(item.programId);
      if (!pid) {
        toast.error('Could not open this program.');
        return;
      }
      navigate(`/fitness/program/${pid}`);
      return;
    }
    if (kind === 'exercise') {
      const eid = resolveExerciseRouteId(item.exerciseId);
      if (!eid) {
        toast.error('Could not open this exercise.');
        return;
      }
      navigate(`/fitness/exercise/${eid}`);
      return;
    }
    toast.error('This assignment cannot be opened.');
  };

  const goToCompletedSessionStats = (item) => {
    if (item.status !== 'completed' || !item.sessionId) return;
    const param = resolveSessionRouteParam(item.sessionId);
    if (!param) return;
    navigate(`/fitness/session/${encodeURIComponent(param)}`);
  };

  const openWorkoutSession = async (item) => {
    const kind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
    if (kind !== 'workout') return;
    const wid = resolveScheduledWorkoutRouteId(item, draftByScheduleId);
    if (!wid) {
      toast.error('Could not open this workout. It may be stale after data refresh. Remove and reschedule it.');
      return;
    }

    const hasResolvedWorkout = Boolean(item.workoutId && typeof item.workoutId === 'object' && item.workoutId.name);
    if (!hasResolvedWorkout && !workoutNameByRouteId[wid]) {
      try {
        const response = await fetch(`${API_URL}/fitness/workouts/${encodeURIComponent(wid)}`, {
          headers: getAuthHeaders()
        });
        if (!response.ok) {
          toast.error('This workout link is stale after data refresh. Remove it and reschedule.');
          return;
        }
        const data = await response.json();
        if (data?.name) {
          setWorkoutNameByRouteId(prev => ({ ...prev, [wid]: data.name }));
        }
      } catch {
        toast.error('Could not open this workout right now. Please try again.');
        return;
      }
    }

    navigate(`/fitness/active/${wid}?scheduleId=${item._id}`);
  };

  const hideCoachItemFromMyWorkouts = async (scheduleItemId) => {
    try {
      const response = await fetch(`${API_URL}/fitness/my-schedule/${scheduleItemId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ hiddenFromClient: true })
      });
      const errBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(errBody.error || 'Could not hide item');
        return false;
      }
      toast.success('Hidden from My Workouts');
      return true;
    } catch (error) {
      toast.error('Could not hide item');
      return false;
    }
  };

  const removeFromSchedule = async (id) => {
    try {
      const response = await fetch(`${API_URL}/fitness/my-schedule/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Removed from schedule');
        setSchedule(schedule.filter(s => s._id !== id));
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to remove');
      }
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const runUnsubscribeCoach = async () => {
    if (!unsubscribeTarget) return;
    const name = unsubscribeTarget.coachId?.name || 'this coach';
    setUnsubscribeLoading(true);
    try {
      const data = await unsubscribeFromCoach(unsubscribeTarget._id);
      toast.success(`You are no longer connected with ${name}.`);
      if (data.stripe?.attempted && data.stripe?.cancelled && data.stripe?.canceledSubscriptionId) {
        toast.success('Recurring billing was canceled in Stripe.');
      }
      if (data.stripe?.attempted && !data.stripe?.cancelled && data.stripe?.error) {
        toast.error(`Stripe could not cancel billing: ${data.stripe.error}`);
      }
      setFitnessCoachRels(prev => prev.filter(r => r._id !== unsubscribeTarget._id));
      setUnsubscribeTarget(null);
      fetchAssignments();
      fetchSchedule();
    } catch (err) {
      toast.error(err.message || 'Failed to unsubscribe');
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  const getItemsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return schedule.filter(s => {
      const sDate = format(parseISO(s.scheduledDate), 'yyyy-MM-dd');
      return sDate === dayStr;
    });
  };

  const rescheduleItem = async (itemId, newDate) => {
    try {
      const response = await fetch(`${API_URL}/fitness/my-schedule/${itemId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scheduledDate: newDate })
      });
      if (response.ok) {
        // Update local state immediately — use noon UTC to avoid timezone day-shift
        setSchedule(prev => prev.map(s =>
          s._id === itemId ? { ...s, scheduledDate: newDate + 'T12:00:00.000Z' } : s
        ));
        toast.success('Workout moved');
      }
    } catch (error) {
      toast.error('Failed to move workout');
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newDate = destination.droppableId; // droppableId is yyyy-MM-dd
    rescheduleItem(draggableId, newDate);
  };

  const getItemName = (item) => {
    if (item.programId && typeof item.programId === 'object' && item.programId.name) return item.programId.name;
    if (item.workoutId && typeof item.workoutId === 'object' && item.workoutId.name) return item.workoutId.name;
    if (item.exerciseId && typeof item.exerciseId === 'object' && item.exerciseId.name) return item.exerciseId.name;
    const matchingDraft = draftByScheduleId.get(String(item._id));
    if (matchingDraft?.workoutName) return matchingDraft.workoutName;
    const kind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
    if (kind === 'workout') {
      const routeId = resolveScheduledWorkoutRouteId(item, draftByScheduleId);
      if (routeId && workoutNameByRouteId[routeId]) return workoutNameByRouteId[routeId];
      return 'Workout';
    }
    if (kind === 'program') return 'Program';
    if (kind === 'exercise') return 'Exercise';
    return 'Activity';
  };

  const getWorkoutDurationLabel = (item) => {
    const kind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
    if (kind !== 'workout') return null;

    if (item.workoutId && typeof item.workoutId === 'object') {
      const direct = Number(item.workoutId.estimatedDuration);
      if (Number.isFinite(direct) && direct > 0) {
        return `${Math.round(direct)} min`;
      }
    }

    const routeId = resolveScheduledWorkoutRouteId(item, draftByScheduleId);
    if (!routeId) return null;
    const cached = Number(workoutDurationByRouteId[routeId]);
    if (Number.isFinite(cached) && cached > 0) {
      return `${Math.round(cached)} min`;
    }
    return null;
  };

  const hasSavedWorkout =
    schedule.length > 0 || recentSessions.length > 0 || activeDrafts.length > 0;
  const onboardingItems = [
    {
      id: 'ai_coach',
      label: 'Chat with your AI Coach',
      hint: 'Ask for meal ideas, workout tips, or help building your plan.',
      done: (usageStats?.usage?.aiCoachMessagesThisMonth || 0) > 0,
      action: () => window.dispatchEvent(new CustomEvent('open-ai-coach')),
      actionLabel: 'Open Coach'
    },
    {
      id: 'workout',
      label: 'Save a workout',
      hint: 'Browse exercises and build your first custom workout.',
      done: hasSavedWorkout,
      action: () => navigate('/fitness/workouts'),
      actionLabel: 'Browse Workouts'
    }
  ];
  const onboardingCompleted = onboardingItems.filter(i => i.done).length;
  const showOnboarding =
    subscriptionTier === 'free' &&
    !onboardingDismissed &&
    !loading &&
    onboardingCompleted < onboardingItems.length;

  return (
    <div className="my-workouts-page">
      <div className="my-workouts-header">
        <h1><i className="fas fa-calendar-check"></i> My Workouts</h1>
        <p>Your weekly workout schedule</p>
      </div>

      {showOnboarding && (
        <div className="user-onboarding-checklist">
          <div className="user-onboarding-header">
            <div>
              <h3>🏋️ Get Started with Fitness</h3>
              <p className="user-onboarding-progress-text">
                {onboardingCompleted} of {onboardingItems.length} steps complete
              </p>
            </div>
            <button className="user-onboarding-dismiss" onClick={handleDismissOnboarding} title="Dismiss">✕</button>
          </div>
          <div className="user-onboarding-progress-bar">
            <div
              className="user-onboarding-progress-fill"
              style={{ width: `${(onboardingCompleted / onboardingItems.length) * 100}%` }}
            />
          </div>
          <ul className="user-onboarding-items">
            {onboardingItems.map(item => (
              <li key={item.id} className={`user-onboarding-item ${item.done ? 'done' : ''}`}>
                <span className="user-onboarding-check">
                  {item.done ? '✓' : <span className="user-onboarding-circle" />}
                </span>
                <div className="user-onboarding-item-content">
                  <span className="user-onboarding-item-label">{item.label}</span>
                  {!item.done && <span className="user-onboarding-item-hint">{item.hint}</span>}
                </div>
                {!item.done && (
                  <button className="user-onboarding-item-action" onClick={item.action}>
                    {item.actionLabel}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coach Assignments & connections */}
      {!assignmentsLoading && (assignments.length > 0 || fitnessCoachRels.length > 0) && (
        <div className="coach-assignments-section">
          <h2>
            <i className="fas fa-user-tie"></i> From Your Coach
            {totalCoachUnread > 0 && <span className="coach-heading-unread-badge">{totalCoachUnread}</span>}
          </h2>
          {fitnessCoachRels.length > 0 && (
            <div className="coach-connections-bar">
              <p className="coach-connections-hint">
                Hide individual assignments with the eye icon. Unsubscribe ends your connection with that coach and removes all of their workout assignments from My Workouts (same coach link is used for nutrition, if you have it).
              </p>
              <div className="coach-connections-actions">
                {fitnessCoachRels.map(rel => (
                  <div key={rel._id} className="coach-connection-block">
                    {rel.activeServiceId && typeof rel.activeServiceId === 'object' && (
                      <div className="coach-active-service">
                        <span className="coach-active-service-label">Your coaching plan</span>
                        <div className="coach-active-service-main">
                          <strong className="coach-active-service-name">{rel.activeServiceId.name}</strong>
                          <span className="coach-active-service-price">
                            ${(Number(rel.activeServiceId.price) / 100).toFixed(2)}
                            {rel.activeServiceId.billingInterval === 'monthly' ? '/mo' : ' one-time'}
                          </span>
                        </div>
                        {rel.subscriptionStatus && rel.subscriptionStatus !== 'active' && (
                          <span className="coach-active-service-status">Status: {rel.subscriptionStatus}</span>
                        )}
                        {Array.isArray(rel.activeServiceId.features) && rel.activeServiceId.features.length > 0 && (
                          <ul className="coach-active-service-features">
                            {rel.activeServiceId.features.slice(0, 4).map((f, i) => (
                              <li key={i}><i className="fas fa-check"></i> {f}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <div className="coach-connection-btns">
                      <button
                        type="button"
                        className="coach-message-btn"
                        onClick={() => setMessagingCoachRel(messagingCoachRel === rel._id ? null : rel._id)}
                      >
                        <i className="fas fa-comment-dots"></i> Message {rel.coachId?.name || 'Coach'}
                        {coachUnreadCounts[rel._id] > 0 && (
                          <span className="coach-msg-badge">{coachUnreadCounts[rel._id]}</span>
                        )}
                      </button>
                      <button
                        type="button"
                        className="coach-unsubscribe-btn"
                        onClick={() => setUnsubscribeTarget(rel)}
                      >
                        Unsubscribe from {rel.coachId?.name || 'coach'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {fitnessCoachRels.map(rel => (
                messagingCoachRel === rel._id && (
                  <ChatPanel
                    key={`chat-${rel._id}`}
                    coachClientId={rel._id}
                    currentUserId={currentUser?.id}
                    otherName={rel.coachId?.name || 'Coach'}
                    otherPicture={rel.coachId?.picture || null}
                    onClose={() => {
                      setMessagingCoachRel(null);
                      refreshCoachUnreadCounts();
                    }}
                  />
                )
              ))}
            </div>
          )}
          {assignments.length > 0 && (
          <div className="assignments-list">
            {assignments.map(item => {
              const contentKind = inferAssignmentContent(item);
              const handleCardClick = () => openCoachAssignmentCard(item);
              return (
              <div key={item._id} className="assignment-card" onClick={handleCardClick} style={{ cursor: 'pointer' }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}>
                <div className="assignment-icon">
                  <i className={`fas ${contentKind === 'program' ? 'fa-calendar-alt' : contentKind === 'workout' ? 'fa-list-ol' : contentKind === 'exercise' ? 'fa-running' : 'fa-question'}`}></i>
                </div>
                <div className="assignment-info">
                  <span className="assignment-type">{contentKind || normalizeAssignmentType(item.type) || item.type || 'assignment'}</span>
                  <h4 className="assignment-name">
                    {getItemName(item)}
                  </h4>
                  {item.programId?.description && (
                    <p className="assignment-desc">{item.programId.description}</p>
                  )}
                  {item.workoutId?.description && (
                    <p className="assignment-desc">{item.workoutId.description}</p>
                  )}
                  {item.notes && <p className="assignment-notes"><i className="fas fa-comment"></i> {item.notes}</p>}
                  {item.assignedBy && (
                    <span className="assignment-coach">
                      {item.assignedBy.picture && <img src={item.assignedBy.picture} alt="" className="coach-avatar-sm" />}
                      Assigned by {item.assignedBy.name || 'coach'}
                    </span>
                  )}
                </div>
                <div className="assignment-actions">
                  {currentUser?.sub && canEditOwnedWorkoutOnSchedule(item, currentUser.sub) && (
                    <button
                      type="button"
                      className="assignment-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/fitness/workout/${item.workoutId._id}/edit`);
                      }}
                    >
                      <i className="fas fa-pen"></i> Edit
                    </button>
                  )}
                  {currentUser?.sub && canEditOwnedProgramOnSchedule(item, currentUser.sub) && (
                    <button
                      type="button"
                      className="assignment-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/fitness/program/${item.programId._id}/edit`);
                      }}
                    >
                      <i className="fas fa-pen"></i> Edit
                    </button>
                  )}
                  <button type="button" className="assignment-start-btn" onClick={(e) => { e.stopPropagation(); startAssignment(item); }}>
                    <i className={`fas ${contentKind === 'workout' ? 'fa-play' : 'fa-eye'}`}></i>
                    {contentKind === 'workout' ? 'Start Workout' : contentKind === 'exercise' ? 'View Exercise' : 'View Program'}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* Week Navigator */}
      <div className="week-navigator">
        <button onClick={() => setWeekStart(subWeeks(weekStart, 1))} className="week-nav-btn">
          <i className="fas fa-chevron-left"></i>
        </button>
        <div className="week-label">
          <span className="week-dates">{format(weekStart, 'MMM d')} — {format(weekEnd, 'MMM d, yyyy')}</span>
        </div>
        <button onClick={() => setWeekStart(addWeeks(weekStart, 1))} className="week-nav-btn">
          <i className="fas fa-chevron-right"></i>
        </button>
        <button className="today-btn" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
          Today
        </button>
      </div>

      {loading ? (
        <div className="my-workouts-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading schedule...</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
        <div className="week-grid">
          {weekDays.map(day => {
            const dayItems = getItemsForDay(day);
            const today = isToday(day);
            const dayStr = format(day, 'yyyy-MM-dd');
            return (
              <div key={day.toISOString()} className={`day-column ${today ? 'today' : ''}`}>
                <div className="day-header">
                  <span className="day-name">{format(day, 'EEE')}</span>
                  <span className={`day-date ${today ? 'today-date' : ''}`}>{format(day, 'd')}</span>
                </div>
                <Droppable droppableId={dayStr}>
                  {(provided, snapshot) => (
                    <div
                      className={`day-items ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {dayItems.length === 0 && !snapshot.isDraggingOver && (
                        <div className="day-empty">
                          <span>Rest day</span>
                        </div>
                      )}
                      {dayItems.map((item, index) => (
                        <Draggable
                          key={item._id}
                          draggableId={item._id}
                          index={index}
                          isDragDisabled={item.status === 'completed'}
                        >
                          {(dragProvided, dragSnapshot) => {
                            const rowKind = inferAssignmentContent(item) || normalizeAssignmentType(item.type);
                            const isWorkoutRow = rowKind === 'workout';
                            const hasDraft = draftByScheduleId.has(String(item._id));
                            const primaryLabel = hasDraft || item.status === 'in_progress' ? 'Resume' : 'Start';
                            const completedOpen = item.status === 'completed' && item.sessionId;
                            const durationLabel = getWorkoutDurationLabel(item);
                            return (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              className={`schedule-item status-${item.status} ${dragSnapshot.isDragging ? 'dragging' : ''}`}
                            >
                        <div className="schedule-item-content">
                          <h4 className="schedule-item-name">{getItemName(item)}</h4>
                          {durationLabel && (
                            <span className="schedule-item-duration">
                              <i className="fas fa-clock"></i> {durationLabel}
                            </span>
                          )}
                          {item.assignedBy && (
                            <span className="assigned-by">
                              <i className="fas fa-user"></i> Assigned by {item.assignedBy?.name || 'coach'}
                            </span>
                          )}
                        </div>

                        <div className="schedule-item-footer">
                          <div className="schedule-item-actions-left schedule-item-primary-stack">
                            <button
                              type="button"
                              className="schedule-action-btn view-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCoachAssignmentCard(item);
                              }}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                            {isWorkoutRow && item.status !== 'completed' && (
                              <button
                                className={`schedule-action-btn ${primaryLabel === 'Resume' ? 'resume-btn' : 'start-btn'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openWorkoutSession(item);
                                }}
                              >
                                <i className={`fas ${primaryLabel === 'Resume' ? 'fa-redo' : 'fa-play'}`}></i> {primaryLabel}
                              </button>
                            )}
                            {completedOpen && (
                              <button
                                type="button"
                                className="summary-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  goToCompletedSessionStats(item);
                                }}
                              >
                                <i className="fas fa-chart-line"></i> Summary
                              </button>
                            )}
                          </div>

                          <div className="schedule-item-actions-right">
                            {item.status !== 'completed' && (
                              <button
                                type="button"
                                className="remove-btn"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (item.assignedBy) {
                                    const ok = await hideCoachItemFromMyWorkouts(item._id);
                                    if (ok) fetchSchedule();
                                  } else {
                                    removeFromSchedule(item._id);
                                  }
                                }}
                                title={item.assignedBy ? 'Hide from My Workouts' : 'Remove'}
                              >
                                <i className={`fas ${item.assignedBy ? 'fa-eye-slash' : 'fa-times'}`}></i>
                              </button>
                            )}
                          </div>
                        </div>
                            </div>
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
        </DragDropContext>
      )}

      {/* Progression Tracking */}
      <ProgressionSection />

      {/* Recent Workout History */}
      <div className="recent-history-section">
        <h2><i className="fas fa-history"></i> Recent Workouts</h2>
        {activeDrafts.length > 0 && (
          <div className="active-drafts-section">
            <h3><i className="fas fa-redo"></i> Resume Workout</h3>
            <div className="recent-sessions-list">
              {activeDrafts.map(draft => (
                <div
                  key={draft.draftKey}
                  className="recent-session-card active-draft-card"
                  onClick={() => navigate(`/fitness/active/${draft.workoutId}${draft.scheduleId ? `?scheduleId=${draft.scheduleId}` : ''}`)}
                >
                  <div className="session-icon active-draft-icon">
                    <i className="fas fa-play"></i>
                  </div>
                  <div className="session-info">
                    <h4>{draft.workoutName || 'Workout in progress'}</h4>
                    <span className="session-date">
                      Last updated {format(parseISO(draft.updatedAt), 'MMM d, yyyy h:mm a')}
                    </span>
                  </div>
                  <div className="session-meta">
                    <span className="session-progress">{draft.completedExercises || 0}/{draft.totalExercises || 0} exercises</span>
                    <span className="session-progress">{draft.progress || 0}% complete</span>
                    <span className="resume-pill">In progress</span>
                  </div>
                  <i className="fas fa-chevron-right session-arrow"></i>
                </div>
              ))}
            </div>
          </div>
        )}
        {sessionsLoading ? (
          <p className="history-loading">Loading history...</p>
        ) : recentSessions.length === 0 ? (
          <p className="history-empty">No workout sessions yet. Start your first workout!</p>
        ) : (
          <div className="recent-sessions-list">
            {recentSessions.map(session => (
              <div
                key={session.sessionId || session._id}
                className="recent-session-card"
                onClick={() => navigate(`/fitness/session/${session.sessionId || session._id}`)}
              >
                <div className="session-icon">
                  <i className="fas fa-dumbbell"></i>
                </div>
                <div className="session-info">
                  <h4>{session.workoutName || 'Workout'}</h4>
                  <span className="session-date">{format(parseISO(session.startedAt || session.createdAt), 'MMM d, yyyy')}</span>
                </div>
                <div className="session-meta">
                  {session.duration && <span className="session-duration"><i className="fas fa-clock"></i> {Math.round(session.duration / 60)}m</span>}
                  <span className="session-exercises">{session.exercisesCompleted || 0} exercises</span>
                  {(() => {
                    const logs = session.exerciseLogs?.length > 0 ? session.exerciseLogs : session.exercises || [];
                    const weights = logs
                      .flatMap(l => (l.sets || []).map(s => s.weight))
                      .filter(w => w && w > 0);
                    if (weights.length === 0) return null;
                    const maxW = Math.max(...weights);
                    return <span className="session-weights"><i className="fas fa-weight-hanging"></i> up to {gymKgToDisplay(maxW, weightUnit).value} {weightUnit}</span>;
                  })()}
                </div>
                <i className="fas fa-chevron-right session-arrow"></i>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!unsubscribeTarget}
        title={unsubscribeTarget ? `Unsubscribe from ${unsubscribeTarget.coachId?.name || 'this coach'}?` : 'Unsubscribe?'}
        message="All workouts and programs they assigned will be removed from My Workouts. This ends your coaching connection (the same link is used for nutrition, if you have it)."
        confirmLabel="Unsubscribe"
        cancelLabel="Cancel"
        danger
        loading={unsubscribeLoading}
        loadingLabel="Unsubscribing…"
        onConfirm={runUnsubscribeCoach}
        onCancel={() => !unsubscribeLoading && setUnsubscribeTarget(null)}
      />
    </div>
  );
};

export default MyWorkoutsPage;
